// The generic form step.
//
// This one component renders *every* schema-driven screen in every vertical:
// it resolves the current step from the route, loads the saved case record,
// merges in any OCR extraction, renders the schema, and on submit saves and
// advances to the next step in the config. No per-screen page component is
// needed, which is what makes adding a step a config-only change.
//
// Screens with bespoke bodies (plan lists, compare, document upload) opt out
// by having no `form` in their config and rendering their own children.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Snackbar } from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import JourneyShell from "../layouts/JourneyShell";
import { SchemaForm, useSchemaForm } from "../../form";
import ocrPrefill, { ocrPrefillCount } from "../../form/ocrPrefill";
import { findStepByPath, nextPath } from "../../config/journeys";
import { getDataAdapter } from "../../config/dataAdapters";
import { recordAnswers } from "../../redux/actions";

// Every schema-driven route renders this same component, so React reconciles
// them as one instance and carries the form's state across a step change — a
// failed validation on step 1 left step 2 showing "this field is required"
// before the customer had typed anything, and stale values leaked with it.
// Keying on the pathname forces a clean mount per step.
const JourneyStep = () => {
  const location = useLocation();
  return <JourneyStepBody key={location.pathname} />;
};

const JourneyStepBody = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const caseId = useSelector((/** @type {import("../../redux/types").RootState} */ state) => state.caseId);
  const insuranceType = useSelector((/** @type {import("../../redux/types").RootState} */ state) => state.insuranceType);
  const ocr = useSelector((/** @type {import("../../redux/types").RootState} */ state) => state.extractedOcrData);

  const match = useMemo(
    () => findStepByPath(location.pathname, insuranceType),
    [location.pathname, insuranceType]
  );

  const [record, setRecord] = useState(/** @type {Record<string, *>} */ ({}));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const schema = match?.step?.form;
  const journeyKey = match?.journey?.key;

  // Saved answers first, OCR on top: a document read is newer than whatever
  // was stored on a previous visit, and the hook then refuses to overwrite
  // anything the user has already touched in this session.
  const initial = useMemo(
    () => ({ ...record, ...ocrPrefill(schema, ocr) }),
    [record, schema, ocr]
  );

  const form = useSchemaForm({ schema, initial });

  // How many fields this step actually got from the uploaded documents. Zero
  // when the read failed or filled nothing on *this* step, so the notice can
  // never claim a success that didn't happen.
  const prefilled = useMemo(() => ocrPrefillCount(schema, ocr), [schema, ocr]);

  useEffect(() => {
    if (!journeyKey) return;
    const adapter = getDataAdapter(journeyKey);
    if (!adapter || !caseId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    adapter
      .load(caseId)
      .then((data) => !cancelled && setRecord(data || {}))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [journeyKey, caseId, location.pathname]);

  const advance = useCallback(async () => {
    if (!form.validateAll()) {
      // Scroll the first invalid control into view — on a long form the error
      // is often above the fold the CTA lives in.
      const firstError = /** @type {HTMLElement | null} */ (
        document.querySelector('[aria-invalid="true"]')
      );
      firstError?.scrollIntoView?.({ behavior: "smooth", block: "center" });
      firstError?.focus?.();
      return;
    }

    const adapter = getDataAdapter(journeyKey);
    setSaving(true);
    setError("");
    try {
      const values = form.payload();
      if (adapter && caseId) await adapter.save(caseId, values);
      // Feed the navigation ribbon, which renders these answers as the chips
      // you can click to change them later.
      dispatch(recordAnswers(journeyKey, values));

      // Some answers are also session state that other screens read directly
      // from redux — the travel direction picks which upstream product line
      // the quote calls. The step declares that mapping rather than any screen
      // hard-coding it.
      for (const [field, type] of Object.entries(match.step.dispatchOnSave || {})) {
        if (values[field] !== undefined) dispatch({ type, payload: values[field] });
      }
      const target = nextPath(journeyKey, match.index);
      if (target) navigate(target);
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          "We couldn't save your answers. Please check your connection and try again."
      );
    } finally {
      setSaving(false);
    }
  }, [form, journeyKey, caseId, match, navigate, dispatch]);

  // Enter advances from any single-line input, which is the keyboard half of
  // replacing the Proceed button. Textareas keep their newline behaviour.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== "Enter" || event.shiftKey) return;
      const tag = event.target?.tagName;
      if (tag === "TEXTAREA" || tag === "BUTTON") return;
      event.preventDefault();
      // Invalid is fine — `advance` surfaces the errors rather than silently
      // doing nothing, which is what the CTA does too.
      advance();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [advance]);

  // A `choice` step with `hideCta` advances on selection — picking the card
  // *is* the navigation, so there is nothing left to press.
  const soleChoice = useMemo(() => {
    if (!match?.step?.hideCta || !schema) return null;
    const fields = schema.sections.flatMap((s) => s.fields);
    return fields.length === 1 && fields[0].type === "choice" ? fields[0] : null;
  }, [match, schema]);

  // Only a *user's* pick advances. Hydrating the saved answer must not, or
  // clicking this step in the stepper to change your mind would bounce you
  // straight forward again without ever showing you the choice.
  const choiceValue = soleChoice ? form.values[soleChoice.name] : undefined;
  const hydratedChoice = useRef(/** @type {string | undefined} */ (undefined));
  useEffect(() => {
    if (loading) return;
    if (hydratedChoice.current === undefined) {
      hydratedChoice.current = choiceValue ?? "";
      return;
    }
    if (!soleChoice || !choiceValue || saving) return;
    if (choiceValue === hydratedChoice.current) return;
    advance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [choiceValue, loading]);

  if (!match || !schema) return null;

  return (
    <JourneyShell
      loading={loading}
      cta={{
        label: match.step.cta || "Continue",
        onClick: advance,
        // Deliberately NOT gated on validity. A disabled CTA is a dead end:
        // errors stay hidden until a field is blurred or submit is tried, and
        // the progress ring counts blankness rather than validity — so a step
        // whose only fault is a cross-step mismatch reads "all done" next to a
        // button that does nothing. Pressing it runs `advance`, which reveals
        // every error and scrolls to the first one.
        busy: saving,
        progress: form.progress,
        nextIncomplete: form.nextIncomplete,
      }}
    >
      {prefilled > 0 && (
        <Alert
          severity="success"
          icon={<AutoAwesomeRoundedIcon fontSize="inherit" />}
          sx={{ mb: 1, borderRadius: 2 }}
        >
          We filled in {prefilled} {prefilled === 1 ? "field" : "fields"} from your
          documents. Please check they're right.
        </Alert>
      )}

      <SchemaForm schema={schema} form={form} disabled={saving} />

      <Snackbar
        open={!!error}
        autoHideDuration={8000}
        onClose={() => setError("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setError("")} sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      </Snackbar>
    </JourneyShell>
  );
};

export default JourneyStep;
